/*
 * The admin site's words in Danish: the editor section - line for line
 * editor.en.ts, which is the contract.
 *
 * `noun` arrives as the English kind, 'article' or 'release note'; each line
 * that takes it chooses its own Danish forms (artikel/artiklen/artiklens,
 * udgivelsesnote/udgivelsesnoten/udgivelsesnotens - both common gender) rather
 * than having a translated noun spliced into it.
 */

import type { VideoProvider } from '@accotool-kb/shared';
import type { editorEn } from './editor.en.js';

const isReleaseNote = (noun: string): boolean => noun === 'release note';

/** 'artikel', 'udgivelsesnote'. */
const indefinite = (noun: string): string => (isReleaseNote(noun) ? 'udgivelsesnote' : 'artikel');

/** 'artiklen', 'udgivelsesnoten'. */
const definite = (noun: string): string => (isReleaseNote(noun) ? 'udgivelsesnoten' : 'artiklen');

/** 'én gang', '3 gange'. */
const times = (count: number): string => (count === 1 ? 'én gang' : `${count} gange`);

const PROVIDERS: Record<VideoProvider, string> = {
  youtube: 'YouTube-video',
  vimeo: 'Vimeo-video',
  file: 'Videofil',
};

export const editorDa: typeof editorEn = {
  toolbar: {
    label: 'Formatering',
    bold: 'Fed',
    italic: 'Kursiv',
    inlineCode: 'Inline-kode',
    heading2: 'Overskrift 2',
    heading3: 'Overskrift 3',
    bulletedList: 'Punktopstilling',
    numberedList: 'Nummereret liste',
    quote: 'Citat',
    codeBlock: 'Kodeblok',
    table: 'Tabel',
    externalLink: 'Eksternt link',
    articleLink: (noun) =>
      noun === 'article' ? 'Link til en anden artikel i vidensbasen' : 'Link til en artikel i vidensbasen',
    insertImage: 'Indsæt billede',
    insertImageHint: 'Indsæt billede. Du kan også sætte et ind fra udklipsholderen eller trække det ind i teksten.',
    insertVideo: 'Indsæt video',
    insertVideoHint: 'Indsæt video: YouTube, Vimeo eller et direkte link til en videofil',
    view: 'Visning',
    write: 'Skriv',
    split: 'Opdelt',
    preview: 'Forhåndsvisning',
  },

  inserted: {
    boldText: 'fed tekst',
    italicText: 'kursiv tekst',
    code: 'kode',
    linkText: 'linktekst',
    tableColumn: 'Kolonne',
    tableValue: 'Værdi',
  },

  editor: {
    saveFirstTitle: (noun) => `Gem ${definite(noun)}, før du tilføjer et billede.`,
    saveFirstBody: (noun) =>
      `Et billede gemmes på den ${indefinite(noun)}, det hører til, og denne ${indefinite(noun)} findes ikke endnu. ` +
      'Gem den én gang, og sæt så billedet ind eller træk det ind igen.',
    replaceStored: 'Erstat den gemte fil',
    bodyLabel: (noun) => (isReleaseNote(noun) ? 'Udgivelsesnotens tekst, i markdown' : 'Artiklens tekst, i markdown'),
    placeholder: (noun) =>
      `Skriv ${definite(noun)} i markdown.\n\n` +
      (noun === 'article'
        ? 'Brug artikellink-knappen ovenfor til at henvise til en anden artikel; '
        : 'Brug artikellink-knappen ovenfor til at linke til en artikel; ') +
      'linket følger med, selv hvis artiklens adresse ændres.\n\n' +
      'Sæt et skærmbillede ind, eller træk det direkte ind i teksten for at tilføje det, og skriv så ' +
      'alt-teksten, der hvor markøren står.',
    pastedImage: 'indsat billede',
    fileEmpty: 'Filen er tom.',
    imageTooLarge: (size, limit) => `Billedet fylder ${size}. ${limit}`,
    fileUnreadable: 'Filen kunne ikke læses.',
    notAnImage: 'Filen er ikke et understøttet billede.',
    uploadFailed: 'Billedet kunne ikke uploades.',
  },

  uploads: {
    uploading: (fileName) => `Uploader ${fileName}`,
    percent: (percent) => `${percent} %`,
    failed: 'Filen kunne ikke uploades.',
  },

  audit: {
    title: (noun) => `Tjek billederne i denne ${indefinite(noun)}`,
    notAttached: 'Nævnt i teksten, men ikke tilføjet: {names}. Sæt filen ind eller træk den hertil, eller ret navnet.',
    badName: 'Kan ikke bruges som navn på et billede: {names}. {requirement}',
    notLoaded: 'Disse billedadresser bliver slet ikke indlæst:',
    empty: '(tom)',
    unsafe: {
      empty: 'der står intet filnavn mellem parenteserne.',
      scheme: 'kun http- og https-adresser indlæses; et data:- eller javascript:-billede afvises.',
      protocolRelative:
        'en adresse, der begynder med //, indlæses fra et andet websted med den protokol, siden selv blev hentet med. Skriv den fuldt ud, med https://.',
      path: 'et tilføjet billede angives med et rent filnavn uden mapper, forespørgsel eller fragment.',
      other: 'det er hverken et filnavn eller en fuld https-adresse.',
    },
    missingAlt: (count, named) =>
      `${count === 1 ? 'Ét billede mangler' : `${count} billeder mangler`} alt-tekst${named ? ' ({names})' : ''}. ` +
      'Beskriv, hvad billedet viser, for læsere med skærmlæser og for alle, når det ikke kan indlæses.',
  },

  videoDialog: {
    title: 'Indsæt en video',
    cancel: 'Annuller',
    insert: 'Indsæt',
    linkLabel: 'Videolink',
    prompt: 'Indsæt et link fra YouTube eller Vimeo eller et direkte link til en videofil.',
    recognised: (provider) => `${PROVIDERS[provider]} — bliver indlejret i artiklen.`,
    notEmbeddable:
      'Linket kan ikke indlejres. Understøttet: YouTube, Vimeo eller et direkte https-link til en .mp4-, .m4v-, .webm-, .ogv- eller .ogg-fil.',
  },

  articlePicker: {
    title: (noun) => (noun === 'article' ? 'Link til en anden artikel' : 'Link til en artikel'),
    tokenHint: (noun) => `Indsætter et token, der slås op, når ${definite(noun)} læses, ikke når den skrives.`,
    cancel: 'Annuller',
    insertLink: 'Indsæt link',
    findLabel: 'Find en artikel',
    findPlaceholder: 'Titel, slug eller brødtekst…',
    searching: 'Søger…',
    noMatch: 'Ingen artikler matcher.',
    untitled: '(uden titel)',
    linkTextLabel: 'Linktekst (valgfri)',
    linkTextPlaceholder: 'Lad feltet stå tomt for at bruge målartiklens titel',
    linkTextHint:
      'Uden linktekst ser læseren målartiklens aktuelle titel, så når artiklen omdøbes, opdateres alle links til den.',
  },

  images: {
    heading: 'Billeder',
    ceiling: (noun, max) => `En ${indefinite(noun)} kan højst have ${max} billeder.`,
    noneAttached: 'ingen tilføjet',
    count: (count, max, size) => `${count} af ${max} · ${size}`,
    refresh: 'Opdater',
    refreshHint: 'Spørg serveren, hvilke billeder der er tilføjet nu',
    allUsed: 'Teksten henviser til alle tilføjede billeder.',
    deleteUnusedHint: 'Slet alle billeder, som teksten ikke henviser til',
    deleteUnused: (count) => (count === 0 ? 'Slet ubrugte' : `Slet ${count} ubrugte`),
    emptyHint: (noun) =>
      'Sæt et skærmbillede ind i teksten ovenfor, eller træk det derind, for at tilføje det. ' +
      `Det gemmes på denne ${indefinite(noun)} og hentes derfra, så der er intet at uploade andre steder.`,
    notUsed: 'ikke brugt',
    notUsedHint: 'Intet i teksten henviser til det, så ingen læser vil nogensinde se det.',
    used: (count) => `brugt ${times(count)}`,
    insert: 'Indsæt',
    insertHint: 'Indsæt en henvisning til billedet ved markøren',
    delete: 'Slet',
    deleteTitle: (fileName) => `Slet ${fileName}`,
    nothingChangedYet: 'Intet er ændret endnu.',
    cancel: 'Annuller',
    deleting: 'Sletter…',
    deleteImage: 'Slet billedet',
    stillShown: (count) => `Teksten, du redigerer, viser stadig dette billede ${times(count)}.`,
    stillShownBody:
      'Henvisningerne kommer til at pege på ingenting. Læserne får en mærket pladsholder med alt-teksten, der ' +
      'hvor billedet var, så siden ikke går i stykker - men der vises heller ikke noget. Fjern også ' +
      'henvisningerne fra teksten.',
    details: '{size} · {type}, uploadet af {by} den {date}.',
    deleteBody: (noun) =>
      `Et billede hører til ${definite(noun)} og ikke til en revision, så det fjernes fra alle revisioner, ` +
      'der viste det, også den, læserne ser nu. Filen er væk for altid; den eneste vej tilbage er at uploade ' +
      'den igen.',
    deleted: (fileName, danglingReferences) =>
      danglingReferences > 0
        ? `${fileName} blev slettet. Den udgivne tekst henviser stadig til det ${times(danglingReferences)}, ` +
          'så læserne ser en pladsholder, indtil du fjerner henvisningen og gemmer.'
        : `${fileName} blev slettet.`,
    bulkTitle: (count) => (count === 1 ? 'Slet det ubrugte billede' : `Slet ${count} ubrugte billeder`),
    bulkConfirm: (count) => (count === 1 ? 'Slet billedet' : `Slet ${count} billeder`),
    savedStillShows: (count) =>
      `Den gemte tekst viser stadig ${count === 1 ? 'ét af disse billeder' : `${count} af disse billeder`}.`,
    savedStillShowsBody:
      'Du har fjernet henvisningerne i editoren, men har ikke gemt endnu. Indtil du gemmer, får læserne en ' +
      'mærket pladsholder med alt-teksten, der hvor hvert billede var - siden går ikke i stykker, men der ' +
      'vises heller ikke noget.',
    bulkIntro: (count, size) =>
      `Intet i teksten henviser til ${count === 1 ? 'dette billede' : 'disse billeder'} (${size} i alt), ` +
      `så ingen læser kan se ${count === 1 ? 'det' : 'dem'}:`,
    bulkBody: (noun) =>
      `Et billede hører til ${definite(noun)} og ikke til en revision, så hvert af dem fjernes fra alle ` +
      'revisioner, der viste det, også den, læserne ser nu. Filerne er væk for altid; den eneste vej tilbage ' +
      'er at uploade dem igen.',
    bulkDeleted: (count, size) =>
      count === 0 ? 'Intet blev slettet.' : `${count === 1 ? '1 billede' : `${count} billeder`} blev slettet (${size}).`,
    bulkAlreadyGone: (count) =>
      `${count} var allerede væk - en anden kan have slettet ${count === 1 ? 'det' : 'dem'}.`,
    bulkDangling: (count) =>
      `Den udgivne tekst henviser stadig til ${count === 1 ? 'ét af dem én gang' : `dem ${count} gange`}, ` +
      'så læserne ser pladsholdere, indtil du fjerner henvisningerne og gemmer.',
  },

  attachments: {
    heading: 'Vedhæftede filer',
    noneListed: 'ingen på listen',
    count: (count, max) => `${count} af ${max}`,
    addFromLibrary: 'Tilføj fra biblioteket',
    addFromLibraryHint: 'Sæt en fil, som biblioteket allerede har, på listen',
    full: 'Artiklen har allerede så mange vedhæftede filer på listen, som den må.',
    uploadNew: 'Upload ny',
    uploadNewHint: 'Upload en fil til biblioteket, og sæt den på listen her',
    chooseFiles: 'Vælg filer, der skal vedhæftes',
    uploaded: (fileName) => `${fileName} blev uploadet og sat på artiklens liste.`,
    unlisted: (fileName, linked) =>
      `${fileName} er ikke længere på artiklens liste. Filen er stadig i biblioteket` +
      (linked ? ', og linket til den i teksten virker stadig.' : '.'),
    listed: (fileName) => `${fileName} er nu på artiklens liste.`,
    empty:
      'Intet er på listen. Filer på listen her kan hentes under artiklen; en fil kan også linkes fra teksten ' +
      'uden at stå på listen.',
    alsoListedBy: (others) => `også på listen i ${others === 1 ? '1 anden artikel' : `${others} andre artikler`}`,
    linkedInText: 'linket i teksten',
    download: 'Hent',
    insertLink: 'Indsæt link',
    insertLinkHint: 'Indsæt et link til filen ved markøren',
    remove: 'Fjern',
    removeHint: 'Tag den af artiklens liste; biblioteket beholder filen',
    noStore: 'Denne installation har intet lager til vedhæftede filer.',
    noStoreBody:
      'Filer kan hverken uploades eller hentes, før {setting} er sat på webstedet. Det, der allerede er på listen, vises.',
  },

  attachmentLibrary: {
    title: 'Tilføj en fil fra biblioteket',
    sharedHint: 'Filen deles, den kopieres ikke: erstattes den i biblioteket, erstattes den også her.',
    findLabel: 'Find en fil',
    findPlaceholder: 'Filnavn…',
    loading: 'Indlæser biblioteket…',
    allListed: 'Alle filer i biblioteket er allerede på listen her.',
    noMatch: 'Ingen filer i biblioteket matcher.',
    listedBy: (count) => `på listen i ${count === 1 ? '1 artikel' : `${count} artikler`}`,
    add: 'Tilføj',
    addFile: (fileName) => `Tilføj ${fileName}`,
  },

  preview: {
    unresolved: (count) =>
      `${count === 1 ? '1 artikellink' : `${count} artikellinks`} kunne ikke slås op`,
    linkFailure: {
      unwritten: 'artiklen er ikke skrevet endnu, så der er intet at linke til.',
      notPublished: 'artiklen findes, men er ikke udgivet, så læserne kan ikke følge linket.',
      notFound: 'ingen artikel har det id eller den kombination af produkt og slug.',
      other: 'henvisningen kunne ikke slås op.',
    },
    unresolvedBody: 'Læserne ser linkteksten som almindelig tekst i stedet for et link.',
    notLive: (count) =>
      count === 1
        ? '1 link peger på en artikel, læserne ikke kan se endnu'
        : `${count} links peger på artikler, læserne ikke kan se endnu`,
    notLiveBody:
      'De virker her, fordi du er administrator. For en læser vises de som almindelig tekst, indtil ' +
      'målartiklen er udgivet.',
    missingAttachments: (count) =>
      count === 1
        ? '1 link til en vedhæftet fil kunne ikke slås op'
        : `${count} links til vedhæftede filer kunne ikke slås op`,
    noSuchAttachment: 'ingen vedhæftet fil i biblioteket har det id.',
    notAnAttachmentId: 'målet er ikke id’et på en vedhæftet fil.',
    missingAttachmentsBody: 'Læserne ser linkteksten som almindelig tekst eller slet intet, hvor der ikke er nogen.',
  },
};
